import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../features/account/context/AuthContext";
import LoginButton from "../../features/account/components/LoginButton";
import UserPanel from "../../features/account/components/UserPanel";
import CartIcon from "../../features/market/components/CartIcon";
import useDismissibleLayer from "../../shared/hooks/useDismissibleLayer";
import { useTranslation } from "../../shared/hooks/useTranslation";
import LanguageSwitcher from "../../shared/ui/LanguageSwitcher";
import serverLogo from "../../assets/logos/server.png";
import { ROUTES } from "../router/routes";
import "./Header.css";

const NAV_ITEMS = [
  {
    key: "home",
    labelKey: "footer.home",
    to: ROUTES.HOME,
    isActive: (pathname) => pathname === ROUTES.HOME,
    mobileOnly: true,
  },
  {
    key: "news",
    labelKey: "header.news",
    to: ROUTES.NEWS,
    isActive: (pathname) => pathname.startsWith(ROUTES.NEWS),
  },
  {
    key: "servers",
    labelKey: "header.servers",
    to: ROUTES.SERVERS,
    isActive: (pathname) => pathname.startsWith(ROUTES.SERVERS),
  },
  {
    key: "roadmap",
    labelKey: "header.roadmap",
    fallbackLabel: {
      en: "ROADMAP",
      uk: "ДОРОЖНЯ КАРТА",
    },
    to: ROUTES.ROADMAP,
    isActive: (pathname) => pathname.startsWith(ROUTES.ROADMAP),
  },
  {
    key: "faq",
    labelKey: "header.faq",
    to: ROUTES.FAQ,
    isActive: (pathname) => pathname.startsWith(ROUTES.FAQ),
  },
  {
    key: "market",
    labelKey: "header.market",
    to: ROUTES.MARKET,
    isActive: (pathname) => pathname.startsWith(ROUTES.MARKET),
  },
];

const DESKTOP_NAV_ITEMS = NAV_ITEMS.filter((item) => !item.mobileOnly);
const BURGER_BREAKPOINT = "(max-width: 1040px)";

const getItemLabel = (item, t, locale) => {
  const fallbackLabel =
    typeof item.fallbackLabel === "string"
      ? item.fallbackLabel
      : item.fallbackLabel?.[locale];

  return t(item.labelKey, fallbackLabel);
};

const HeaderNav = ({
  items,
  pathname,
  t,
  locale,
  className,
  onNavigate,
  isMobile,
}) => (
  <>
    {items.map((item, index) => (
      <Link
        key={item.key}
        className={`${className}${item.isActive(pathname) ? " active" : ""}`}
        to={item.to}
        onClick={onNavigate}
        style={
          isMobile
            ? { "--sr-mobile-nav-delay": `${(index + 1) * 0.1}s` }
            : undefined
        }
      >
        {getItemLabel(item, t, locale)}
      </Link>
    ))}
  </>
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const { user, logout, initialized } = useAuth();
  const { locale, t } = useTranslation();
  const { pathname } = useLocation();
  const isMarketPage = pathname.startsWith(ROUTES.MARKET);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 0);

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  useDismissibleLayer({
    isOpen: isMobileMenuOpen,
    onClose: closeMobileMenu,
    containerRef: mobileMenuRef,
    returnFocusRef: mobileMenuButtonRef,
    lockBodyScroll: true,
    closeOnPointerDownOutside: false,
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(BURGER_BREAKPOINT);
    const closeMenuOnDesktop = (event) => {
      if (!event.matches) {
        closeMobileMenu();
      }
    };

    closeMenuOnDesktop(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", closeMenuOnDesktop);
      return () => mediaQuery.removeEventListener("change", closeMenuOnDesktop);
    }

    mediaQuery.addListener(closeMenuOnDesktop);
    return () => mediaQuery.removeListener(closeMenuOnDesktop);
  }, [closeMobileMenu]);

  useEffect(() => {
    closeMobileMenu();
  }, [closeMobileMenu, pathname]);

  return (
    <header className={`sr-header${isScrolled ? " scrolled" : ""}`}>
      <div className="sr-header-inner">
        <div className="sr-left">
          <button
            ref={mobileMenuButtonRef}
            className={`sr-mobile-menu-toggle ${isMobileMenuOpen ? "active" : ""}`}
            onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
            aria-label={
              isMobileMenuOpen
                ? "Close navigation menu"
                : "Open navigation menu"
            }
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
            type="button"
          >
            <span className="sr-hamburger-line"></span>
            <span className="sr-hamburger-line"></span>
            <span className="sr-hamburger-line"></span>
          </button>

          <Link
            to={ROUTES.HOME}
            className={`sr-nav-box${pathname === ROUTES.HOME ? " active" : ""}`}
            aria-label="Home"
          >
            <img
              className="sr-nav-logo"
              src={serverLogo}
              alt="Server logo"
              width="40"
              height="40"
            />
          </Link>

          <nav className="sr-nav" aria-label="Primary">
            <HeaderNav
              items={DESKTOP_NAV_ITEMS}
              pathname={pathname}
              t={t}
              locale={locale}
              className="sr-nav-link"
            />
          </nav>
        </div>

        <div className="sr-right">
          <div className="sr-utilities">
            <LanguageSwitcher />
            {initialized && user && isMarketPage && (
              <CartIcon className="sr-cart-icon" />
            )}
          </div>

          <div className="sr-actions">
            {initialized &&
              (user ? <UserPanel onLogout={logout} /> : <LoginButton />)}
          </div>
        </div>
      </div>

      <div
        ref={mobileMenuRef}
        className={`sr-mobile-menu ${isMobileMenuOpen ? "open" : ""}`}
        aria-hidden={!isMobileMenuOpen}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            closeMobileMenu();
          }
        }}
      >
        <nav
          className="sr-mobile-nav"
          aria-label="Mobile navigation"
          id="mobile-navigation"
        >
          <HeaderNav
            items={NAV_ITEMS}
            pathname={pathname}
            t={t}
            locale={locale}
            className="sr-mobile-nav-link"
            onNavigate={closeMobileMenu}
            isMobile
          />
        </nav>
      </div>
    </header>
  );
};

export default Header;
